import type { Helpers } from "./endpoints-shared";
import type { In, Out } from "./operations";
import type { PageRequest } from "./types";

export interface AccessFilter {
  q?: string | undefined;
  access?: "none" | "invited" | "active" | "revoked" | undefined;
  lifecycle?: string | undefined;
  hasEmail?: boolean | undefined;
}

/** The club's side of the member portal (`/portal/*`): settings, who has access, invitations, resets, change requests. */
export interface AppText {
  title: string;
  sort_order?: number;
  published?: boolean;
}
export interface AppPageInput extends AppText {
  body?: string;
  link?: string | null;
}
export interface AppPersonInput {
  name: string;
  job_title?: string;
  phone?: string | null;
  sort_order?: number;
}
export interface AppItemInput {
  title: string;
  kind: string;
  target_id?: string | null;
  phone?: string | null;
  body?: string | null;
  published?: boolean;
}
export interface AppRateCardInput {
  title: string;
  season_labels: string[];
  season_notes: string[];
  groups: { name: string; rows: { room: string; prices: number[] }[] }[];
  published: boolean;
}

export function portalEndpoints({ http, get, post, patch, put, del, paged }: Helpers) {
  return {
    readSettings: (signal?: AbortSignal) => get<Out<"portal_read_settings">>("/portal/settings", { signal }),
    updateSettings: (input: In<"portal_update_settings">) => patch<Out<"portal_update_settings">>("/portal/settings", input),

    listAccess: (page: PageRequest & AccessFilter = {}) =>
      get<Out<"portal_list_access">>("/portal/access", paged(page, { q: page.q, access: page.access, lifecycle: page.lifecycle, has_email: page.hasEmail })),
    invite: (memberId: string, input: In<"portal_invite_member">) => post<Out<"portal_invite_member">>(`/portal/members/${memberId}/invite`, input),
    /** The invitation email this member would get, in the club's wording, before anything is sent. Makes no account or link. */
    previewInvite: (memberId: string, email?: string, signal?: AbortSignal) =>
      get<Out<"portal_preview_invite">>(`/portal/members/${memberId}/invite/preview`, { signal, query: { email } }),
    inviteMany: (input: In<"portal_invite_many">) => post<Out<"portal_invite_many">>("/portal/invitations/bulk", input),
    revoke: (memberId: string, input: In<"portal_revoke_access"> = {}) => post<void>(`/portal/members/${memberId}/revoke`, input),
    resetPin: (memberId: string, input: In<"portal_reset_pin"> = { send_email: true }) => post<Out<"portal_reset_pin">>(`/portal/members/${memberId}/reset-pin`, input),
    resetPassword: (memberId: string, input: In<"portal_reset_password"> = { send_email: true }) =>
      post<Out<"portal_reset_password">>(`/portal/members/${memberId}/reset-password`, input),
    resetTwoFactor: (memberId: string) => post<void>(`/portal/members/${memberId}/reset-two-factor`),
    /** Lets the nightly Jonas import write this contact detail again (a member's own change protects it until then). */
    releaseProtectedField: (memberId: string, field: "email" | "phone" | "address") => del<void>(`/portal/members/${memberId}/protected-fields/${field}`),

    /** Online payments a person has to look at: the books keep refusing one, or the processor reported a refund or a dispute. */
    paymentsNeedingAttention: (page: PageRequest = {}) => get<Out<"portal_payments_needing_attention">>("/portal/payments/attention", paged(page)),
    retryPayment: (id: string) => post<Out<"portal_retry_posting">>(`/portal/payments/${id}/retry`),
    resolvePayment: (id: string, input: In<"portal_resolve_attention">) => post<Out<"portal_resolve_attention">>(`/portal/payments/${id}/resolve`, input),

    /** Staff "view as member": asks to open the member portal as this member, with the reason the audit log keeps. The answer's `url` is opened in a new tab. */
    startViewAs: (memberId: string, input: In<"portal_start">) => post<Out<"portal_start">>(`/portal/members/${memberId}/view-as`, input),
    endViewAs: (id: string) => post<void>(`/portal/impersonations/${id}/end`),
    listViewAs: (page: PageRequest & { memberId?: string | undefined; staffUserId?: string | undefined; active?: boolean | undefined } = {}) =>
      get<Out<"portal_list_impersonations">>("/portal/impersonations", paged(page, { member_id: page.memberId, staff_user_id: page.staffUserId, active: page.active })),

    /** The inbox of what members send from the portal: house-work requests and general messages. `status`: open (default), closed, new, all or one status. */
    listRequests: (page: PageRequest & { kind?: "maintenance" | "message"; status?: string } = {}) =>
      get<Out<"portal_staff_request_inbox">>("/portal/requests", paged(page, { kind: page.kind, status: page.status })),
    requestCounts: (signal?: AbortSignal) => get<Out<"portal_staff_request_counts">>("/portal/requests/counts", { signal }),
    getRequest: (id: string, signal?: AbortSignal) => get<Out<"portal_staff_get_request">>(`/portal/requests/${id}`, { signal }),
    replyToRequest: (id: string, input: In<"portal_staff_reply_to_request">) => post<Out<"portal_staff_reply_to_request">>(`/portal/requests/${id}/reply`, input),
    closeRequest: (id: string) => post<Out<"portal_staff_close_request">>(`/portal/requests/${id}/close`),

    listChangeRequests: (page: PageRequest & { status?: string } = {}) =>
      get<Out<"portal_list_change_requests">>("/portal/change-requests", paged(page, { status: page.status })),
    approveChange: (id: string, input: In<"portal_approve_change"> = {}) => post<Out<"portal_approve_change">>(`/portal/change-requests/${id}/approve`, input),
    rejectChange: (id: string, input: In<"portal_reject_change"> = {}) => post<Out<"portal_reject_change">>(`/portal/change-requests/${id}/reject`, input),

    /** Galleries, pages, staff directory, menu, announcements and the rate card published to the member app. */
    readMemberApp: (signal?: AbortSignal) => get<MemberAppEditor>("/portal/app", { signal }),
    createGallery: (input: AppText) => post<{ id: string }>("/portal/app/galleries", input),
    uploadGalleryPhoto: (id: string, file: Blob) => http.request<{ id: string }>("POST", `/portal/app/galleries/${id}/photos`, { body: file }),
    deleteGallery: (id: string) => del(`/portal/app/galleries/${id}`),
    createPage: (input: AppPageInput) => post<{ id: string }>("/portal/app/pages", input),
    deletePage: (id: string) => del(`/portal/app/pages/${id}`),
    createStaffGroup: (input: AppText) => post<{ id: string }>("/portal/app/groups", input),
    deleteStaffGroup: (id: string) => del(`/portal/app/groups/${id}`),
    addStaffPerson: (groupId: string, input: AppPersonInput) => post<{ id: string }>(`/portal/app/groups/${groupId}/people`, input),
    uploadStaffPhoto: (id: string, file: Blob) => http.request<{ has_photo: boolean }>("PUT", `/portal/app/people/${id}/photo`, { body: file }),
    createAnnouncement: (input: AppPageInput) => post<{ id: string }>("/portal/app/announcements", input),
    deleteAnnouncement: (id: string) => del(`/portal/app/announcements/${id}`),
    createSection: (input: AppText) => post<{ id: string }>("/portal/app/sections", input),
    deleteSection: (id: string) => del(`/portal/app/sections/${id}`),
    createMenuItem: (sectionId: string, input: AppItemInput) => post<{ id: string }>(`/portal/app/sections/${sectionId}/items`, input),
    deleteMenuItem: (id: string) => del(`/portal/app/items/${id}`),
    saveRateCard: (input: AppRateCardInput) => put<AppRateCardInput & { published: boolean }>("/portal/app/rate-card", input),
  };
}

export interface MemberAppEditor {
  galleries: { id: string; title: string; published: boolean; photos: { id: string; caption: string | null }[] }[];
  pages: { id: string; title: string; body: string; link: string | null; published: boolean }[];
  groups: { id: string; title: string; published: boolean; people: { id: string; name: string; job_title: string; phone: string | null; has_photo: boolean }[] }[];
  announcements: { id: string; title: string; body: string; published: boolean }[];
  sections: { id: string; title: string; published: boolean; items: { id: string; title: string; kind: string; published: boolean }[] }[];
  rate_card: AppRateCardInput | null;
}
